import type { Match } from './queries';
import type { ChannelPick, LinkPick, Selection } from './filters';

export type Sel = { mode: 'in' | 'out'; values: string[] };

/** Every choice (no filter). */
export const ALL_SEL: Sel = { mode: 'out', values: [] };

export type TickState = 'on' | 'off' | 'mixed';

export const isAll = (s: Sel): boolean => s.mode === 'out' && s.values.length === 0;
export const isNone = (s: Sel): boolean => s.mode === 'in' && s.values.length === 0;

/** The selection as it filters: nothing ticked counts as everything. */
export const appliedSel = (s: Sel): Sel => (isNone(s) ? ALL_SEL : s);

/** Whether "All" stands (nothing else picked): no other box reads ticked then. */
const allStands = (s: Sel): boolean => isAll(s) || isNone(s);

/** Whether a flat choice is ticked (never while "All" stands). */
export const tickOf = (s: Sel, value: string): TickState =>
  !allStands(s) && (s.mode === 'in' ? s.values.includes(value) : !s.values.includes(value)) ? 'on' : 'off';

/** The "All" row: ticked while nothing else is picked. */
export const allTick = (s: Sel): TickState => (allStands(s) ? 'on' : 'off');

const without = (values: readonly string[], drop: (v: string) => boolean): string[] => values.filter((v) => !drop(v));
const toggled = (values: readonly string[], value: string): string[] =>
  values.includes(value) ? without(values, (v) => v === value) : [...values, value];

/** A click starts from nothing picked while "All" stands, and settles back on "All" once nothing is. */
const fromAll = (s: Sel): Sel => (allStands(s) ? { mode: 'in', values: [] } : s);
const settle = (s: Sel): Sel => (allStands(s) ? ALL_SEL : s);

/** A flat choice ticked or unticked. */
export const toggleValue = (s: Sel, value: string): Sel => {
  const from = fromAll(s);
  return settle({ mode: from.mode, values: toggled(from.values, value) });
};

/** "All" clicked: it is picked (the other choices clear). */
export const toggleAllSel = (_s: Sel): Sel => ALL_SEL;

export const PICK_SEP = '::';
export const emptyLinks = (): LinkPick => ({ affiliates: [], exact: [], noLink: false, allExcept: null });
export const affiliateOf = (id: string): string | null => {
  const at = id.indexOf('_');
  return at > 0 ? id.slice(0, at) : null;
};
export const channelTick = (picks: ChannelPick[] | null, channel: string): TickState => {
  const pick = picks?.find((p) => p.channel === channel);
  return !pick ? 'off' : pick.platforms === 'all' && pick.except.length === 0 ? 'on' : 'mixed';
};
export const platformTick = (picks: ChannelPick[] | null, channel: string, key: string): TickState => {
  const pick = picks?.find((p) => p.channel === channel);
  return pick && (pick.platforms === 'all' ? !pick.except.includes(key) : pick.platforms.includes(key)) ? 'on' : 'off';
};
export const toggleChannel = (picks: ChannelPick[] | null, channel: ChannelPick['channel']): ChannelPick[] => {
  const rest = (picks ?? []).filter((p) => p.channel !== channel);
  return channelTick(picks, channel) === 'on' ? rest : [...rest, { channel, platforms: 'all', except: [] }];
};
export const togglePlatform = (
  picks: ChannelPick[] | null,
  channel: ChannelPick['channel'],
  key: string,
): ChannelPick[] => {
  const pick = picks?.find((p) => p.channel === channel);
  const rest = (picks ?? []).filter((p) => p.channel !== channel);
  if (pick?.platforms === 'all') return [...rest, { ...pick, except: toggled(pick.except, key) }];
  const platforms = toggled(pick?.platforms ?? [], key);
  return platforms.length ? [...rest, { channel, platforms, except: [] }] : rest;
};
export const linkTick = (pick: LinkPick | null, id: string): TickState => {
  if (!pick) return 'off';
  if (id === '') return pick.noLink ? 'on' : 'off';
  if (pick.allExcept !== null)
    return pick.exact.includes(id) ||
      (!pick.allExcept.includes(id) && !pick.exceptAffiliates?.includes(affiliateOf(id) ?? ''))
      ? 'on'
      : 'off';
  const parent = pick.affiliates.find((a) => a.id === affiliateOf(id));
  return pick.exact.includes(id) || (parent && !parent.exceptLinks.includes(id)) ? 'on' : 'off';
};
export const affiliateTick = (pick: LinkPick | null, id: string, links: readonly string[]): TickState => {
  if (!pick) return 'off';
  if (pick.allExcept !== null) {
    const selected = links.filter((l) => linkTick(pick, l) === 'on').length;
    return selected === links.length && selected > 0 ? 'on' : selected ? 'mixed' : 'off';
  }
  const parent = pick.affiliates.find((a) => a.id === id);
  return parent
    ? parent.exceptLinks.length
      ? 'mixed'
      : 'on'
    : pick.exact.some((l) => affiliateOf(l) === id)
      ? 'mixed'
      : 'off';
};
/** Whether a click on affiliate `id` takes it out of "All known links" but these affiliates: it is left out, and not
 *  ticked back through every one of its `links` (a click on it then leaves it out, see `toggleAffiliate`). */
export const liftsExclusion = (pick: LinkPick | null, id: string, links: readonly string[]): boolean =>
  !!pick && pick.allExcept !== null && !!pick.exceptAffiliates?.includes(id) && affiliateTick(pick, id, links) !== 'on';
export const toggleLink = (current: LinkPick | null, id: string): LinkPick => {
  const pick = current ?? emptyLinks();
  if (id === '') return { ...pick, noLink: !pick.noLink };
  if (pick.allExcept !== null)
    return pick.exceptAffiliates?.includes(affiliateOf(id) ?? '')
      ? { ...pick, exact: toggled(pick.exact, id) }
      : { ...pick, allExcept: toggled(pick.allExcept, id) };
  const parent = pick.affiliates.find((a) => a.id === affiliateOf(id));
  return parent
    ? {
        ...pick,
        affiliates: pick.affiliates.map((a) => (a === parent ? { ...a, exceptLinks: toggled(a.exceptLinks, id) } : a)),
      }
    : { ...pick, exact: toggled(pick.exact, id) };
};
export const toggleAffiliate = (current: LinkPick | null, id: string, links: readonly string[]): LinkPick => {
  const pick = current ?? emptyLinks();
  const on = affiliateTick(pick, id, links) === 'on';
  if (pick.allExcept !== null && pick.exceptAffiliates?.length)
    return {
      ...pick,
      exceptAffiliates: liftsExclusion(pick, id, links)
        ? pick.exceptAffiliates.filter((v) => v !== id)
        : on
          ? [...new Set([...pick.exceptAffiliates, id])]
          : pick.exceptAffiliates,
      allExcept: pick.allExcept.filter((l) => affiliateOf(l) !== id),
      exact: pick.exact.filter((l) => affiliateOf(l) !== id),
    };
  if (pick.allExcept !== null)
    return {
      ...pick,
      allExcept: on ? [...new Set([...pick.allExcept, ...links])] : pick.allExcept.filter((l) => affiliateOf(l) !== id),
    };
  return {
    ...pick,
    affiliates: [...pick.affiliates.filter((a) => a.id !== id), ...(on ? [] : [{ id, exceptLinks: [] }])],
    exact: pick.exact.filter((l) => affiliateOf(l) !== id),
  };
};
export const selectionEmpty = (s: Selection): boolean =>
  (s.channels !== null && !s.channels.some((c) => c.platforms === 'all' || c.platforms.length > 0)) ||
  (s.links !== null &&
    s.links.allExcept === null &&
    !s.links.noLink &&
    !s.links.affiliates.length &&
    !s.links.exact.length);

export const canonicalSelection = (s: Selection): Selection => ({
  channels:
    s.channels === null
      ? null
      : s.channels
          .map((c) => ({
            channel: c.channel,
            platforms: c.platforms === 'all' ? ('all' as const) : [...c.platforms].sort(),
            except: [...c.except].sort(),
          }))
          .sort((a, b) => a.channel.localeCompare(b.channel)),
  links:
    s.links === null
      ? null
      : {
          affiliates: s.links.affiliates
            .map((a) => ({ id: a.id, exceptLinks: [...a.exceptLinks].sort() }))
            .sort((a, b) => a.id.localeCompare(b.id)),
          exact: [...s.links.exact].sort(),
          noLink: s.links.noLink,
          allExcept: s.links.allExcept === null ? null : [...s.links.allExcept].sort(),
          ...(s.links.exceptAffiliates?.length ? { exceptAffiliates: [...s.links.exceptAffiliates].sort() } : {}),
        },
});
export const serializeSelection = (s: Selection): string => JSON.stringify(canonicalSelection(s));
export const resolveMatch = (sel: Sel): Match | null => {
  const s = appliedSel(sel);
  return isAll(s) ? null : { op: s.mode, values: s.values };
};
